import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Award, Day, Pacing, Prize, PrizeColor } from '../../shared/types';
import { DEFAULT_TIMEZONE, PRIZE_COLORS, TIMEZONES } from '../../shared/types';
import { api, ApiError } from '../lib/api';
import { toCsv, untrusted } from '../lib/csv';
import './Config.css';

const SWATCH: Record<PrizeColor, string> = {
  coral: 'var(--seg-coral)',
  mint: 'var(--seg-mint)',
  sky: 'var(--seg-sky)',
  peach: 'var(--seg-peach)',
  lilac: 'var(--seg-lilac)',
  sand: 'var(--seg-sand)',
};

const MINUTE_MS = 60_000;

// Un formateador por zona: se reutilizan en cada render.
const wallFormatters = new Map<string, Intl.DateTimeFormat>();

function wallFormatter(tz: string): Intl.DateTimeFormat {
  let fmt = wallFormatters.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    wallFormatters.set(tz, fmt);
  }
  return fmt;
}

/** La hora de pared que marca `tz` en un instante dado, troceada. */
function wallParts(ms: number, tz: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of wallFormatter(tz).formatToParts(new Date(ms))) out[p.type] = p.value;
  return out;
}

/** ISO -> valor de <input type="datetime-local">, leido en `tz`. */
function toZonedInput(iso: string, tz: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const p = wallParts(ms, tz);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Desfase de `tz` respecto a UTC en un instante, en ms. */
function offsetMs(ms: number, tz: string): number {
  const p = wallParts(ms, tz);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  // `wallParts` solo baja al minuto: se compara contra el instante truncado.
  return asUtc - Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

/** Valor del input, entendido como hora de pared de `tz` -> ISO en UTC. */
function fromZonedInput(value: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return '';
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  // Dos pasadas: la primera estima el desfase, la segunda lo corrige si la
  // estimacion habia caido al otro lado de un cambio de hora.
  let ms = wall - offsetMs(wall, tz);
  ms = wall - offsetMs(ms, tz);
  return new Date(ms).toISOString();
}

/** "PDT", "GMT+2"... para dejar claro con que reloj se esta trabajando. */
function zoneAbbr(tz: string, ms: number): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(
    new Date(ms),
  );
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
}

function formatSlot(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const minutes = ms / MINUTE_MS;
  if (minutes >= 60) return `${(minutes / 60).toFixed(1)} h`;
  if (minutes >= 1) return `${Math.round(minutes)} min`;
  return `${Math.round(ms / 1000)} s`;
}

export function Config() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Los instantes se guardan en ISO/UTC; `timezone` solo dice como se leen.
  const [dias, setDias] = useState<Day[]>([]);
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [premios, setPremios] = useState<Prize[]>([]);
  const [pacing, setPacing] = useState<Pacing[]>([]);
  const [awards, setAwards] = useState<(Award & { label: string })[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const flash = useCallback((kind: 'ok' | 'bad', text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    const { config, pacing } = await api.getConfig();
    setDias(config.dias);
    setTimezone(config.timezone);
    setPremios(config.premios);
    setPacing(pacing);
    setDirty(false);
    const { awards } = await api.awards();
    setAwards(awards);
  }, []);

  useEffect(() => {
    api
      .session()
      .then(({ authed }) => setAuthed(authed))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) load().catch(() => flash('bad', 'Could not load the configuration'));
  }, [authed, load, flash]);

  // El ritmo cambia solo con el tiempo: se refresca sin pisar el formulario.
  useEffect(() => {
    if (!authed) return;
    const id = window.setInterval(() => {
      api
        .getConfig()
        .then(({ pacing }) => setPacing(pacing))
        .catch(() => {});
      api
        .awards()
        .then(({ awards }) => setAwards(awards))
        .catch(() => {});
    }, 20_000);
    return () => window.clearInterval(id);
  }, [authed]);

  /**
   * El log se lee con el reloj del evento, no con el del navegador. Lleva la
   * fecha entera y no solo la hora: el log no se borra nunca, asi que acumula
   * jornadas y un "12:00:00" a secas no dice de que dia habla.
   *
   * Formato en-CA (2026-09-09, 12:00:00) por lo mismo que los campos de fecha
   * de arriba: ano-mes-dia no se puede leer del reves.
   */
  const clockStamp = useMemo(
    () =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }),
    [timezone],
  );

  /**
   * Cuando se dio la autorizacion. Casi siempre son los mismos segundos que
   * el premio, asi que solo se ensena si difiere: si no, seria ruido repetido
   * en cada linea.
   */
  const claimedAt = (a: Award) => {
    if (!a.claimedTs || a.claimedTs === a.ts) return '';
    return ` · ${clockStamp.format(new Date(a.claimedTs))}`;
  };

  // Una zona llegada de un JSON editado a mano tambien tiene que ser elegible.
  const zoneOptions = useMemo(
    () => (TIMEZONES.some((z) => z.id === timezone) ? TIMEZONES : [...TIMEZONES, { id: timezone, label: timezone }]),
    [timezone],
  );

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      await api.login(password);
      setAuthed(true);
    } catch (err) {
      if (err instanceof ApiError) {
        // Si el servidor explica el problema (un secreto sin configurar, por
        // ejemplo), se muestra tal cual: "Connection error" lo escondia.
        setLoginError(err.status === 401 ? 'Wrong password' : err.message);
      } else {
        setLoginError('Connection error');
      }
    }
  };

  const patchPrize = (index: number, patch: Partial<Prize>) => {
    setPremios((list) => list.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    setDirty(true);
  };

  const addPrize = () => {
    const n = premios.length + 1;
    setPremios((list) => [
      ...list,
      { id: `premio_${n}`, label: '', aciertos: 0, color: PRIZE_COLORS[(n - 1) % PRIZE_COLORS.length]! },
    ]);
    setDirty(true);
  };

  const currentConfig = () => ({
    dias,
    timezone,
    premios,
  });

  const setDia = (index: number, patch: Partial<Day>) => {
    setDias((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    setDirty(true);
  };

  /** Una jornada nueva copia el horario de la ultima, un dia despues. */
  const addDia = () => {
    setDias((prev) => {
      const last = prev[prev.length - 1];
      if (!last) {
        const start = new Date();
        start.setHours(10, 0, 0, 0);
        return [{ inicio: start.toISOString(), fin: new Date(start.getTime() + 6 * 3_600_000).toISOString() }];
      }
      const DAY = 86_400_000;
      return [
        ...prev,
        {
          inicio: new Date(Date.parse(last.inicio) + DAY).toISOString(),
          fin: new Date(Date.parse(last.fin) + DAY).toISOString(),
        },
      ];
    });
    setDirty(true);
  };

  const removeDia = (index: number) => {
    setDias((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const save = async () => {
    if (dias.length === 0) return flash('bad', 'The event needs at least one day');
    if (dias.some((d) => !d.inicio || !d.fin)) return flash('bad', 'Every day needs a start and an end');
    setSaving(true);
    try {
      const { pacing } = await api.putConfig(currentConfig());
      setPacing(pacing);
      setDirty(false);
      flash('ok', 'Configuration saved');
    } catch (err) {
      flash('bad', err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const download = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const today = () => new Date().toISOString().slice(0, 10);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ ...currentConfig(), awards }, null, 2)], {
      type: 'application/json',
    });
    download(blob, `spinner-config-${today()}.json`);
  };

  /**
   * El log en CSV, para quien entrega los premios o los carga en el CRM. Salen
   * todas las lineas, tambien las de quien no dejo datos: es una copia fiel del
   * log, y filtrar ya se hace en la hoja de calculo.
   */
  const exportWinners = () => {
    // Sin la coma que mete en-CA: asi la hoja de calculo lo lee como fecha.
    const stamp = (iso: string) => clockStamp.format(new Date(iso)).replace(',', '');
    const csv = toCsv(
      [
        'Prize',
        `Won at (${timezone})`,
        'Name',
        'Email',
        'Authorised',
        `Authorised at (${timezone})`,
      ],
      awards.map((a) => [
        a.label,
        stamp(a.ts),
        // Nombre y correo los teclea el publico: no pueden colar formulas.
        untrusted(a.name ?? ''),
        untrusted(a.email ?? ''),
        a.consent ? 'yes' : 'no',
        a.claimedTs ? stamp(a.claimedTs) : '',
      ]),
    );
    // El BOM es lo que hace que Excel en Windows respete los acentos y el ®.
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
    download(blob, `spinner-winners-${today()}.csv`);
  };

  const importJson = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text());
      const { config, pacing } = await api.putConfig(parsed);
      setDias(config.dias);
      setTimezone(config.timezone);
      setPremios(config.premios);
      setPacing(pacing);
      setDirty(false);
      flash('ok', 'File imported');
    } catch {
      flash('bad', 'That file is not valid JSON');
    }
  };

  if (authed === null) return <div className="login-shell" />;

  if (!authed) {
    return (
      <div className="login-shell">
        <form className="login-card" onSubmit={login}>
          <h1>Prize panel</h1>
          <p>Service Club · Free Fuel Spinner</p>
          <label className="field">
            <span className="field-label">Password</span>
            <input
              className="input"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {loginError && (
            <p className="toast toast-bad" style={{ marginTop: 12 }}>
              {loginError}
            </p>
          )}
          <button className="btn btn-primary" type="submit">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const now = Date.now();
  // Abierto si `now` cae dentro de alguna jornada, no del hueco entre ellas.
  const running = dias.some((d) => now >= Date.parse(d.inicio) && now < Date.parse(d.fin));
  const zoneLabel = zoneOptions.find((z) => z.id === timezone)?.label ?? timezone;
  // La abreviatura se lee en el arranque del evento: si el periodo cae al otro
  // lado de un cambio de hora, "PST" y "PDT" no son lo mismo.
  const primerInicio = dias[0] ? Date.parse(dias[0].inicio) : NaN;
  const abbrAt = Number.isFinite(primerInicio) ? primerInicio : now;

  return (
    <div className="config-page">
      <div className="config-shell">
        <header className="config-head">
          <div>
            <h1>Prize panel</h1>
            <p>
              Times in{' '}
              <strong>
                {zoneLabel} · {zoneAbbr(timezone, abbrAt)}
              </strong>{' '}
              ·{' '}
              <span className={`badge ${running ? 'badge-on' : 'badge-off'}`}>
                {running ? 'GAME LIVE' : 'OUTSIDE GAME HOURS'}
              </span>
            </p>
          </div>
          <div className="actions">
            <a className="btn btn-secondary" href="/spinner" style={{ lineHeight: '42px', textDecoration: 'none' }}>
              View the wheel
            </a>
          </div>
        </header>

        <section className="card">
          <h2>Game period</h2>
          <p className="card-hint">
            One row per day the stand is open. Each prize quota is split between the days in proportion to
            their open hours, and each day is settled on its own: what a day does not hand out stays
            pending and is <strong>not</strong> carried over to the next one. Between days the wheel still
            spins, but it only gives Free Fuel. Times are read in the time zone below; switching zones does
            not move the event, it just shows the same moment on a different clock.
          </p>
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Time zone</span>
              <select
                className="input"
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  setDirty(true);
                }}
              >
                {zoneOptions.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label} — {zoneAbbr(z.id, abbrAt)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="day-list">
            {dias.map((d, i) => (
              <div className="day-row" key={i}>
                <span className="day-name">Day {i + 1}</span>
                <label className="field">
                  <span className="field-label">Start</span>
                  <input
                    className="input"
                    type="datetime-local"
                    value={toZonedInput(d.inicio, timezone)}
                    onChange={(e) => setDia(i, { inicio: fromZonedInput(e.target.value, timezone) })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">End</span>
                  <input
                    className="input"
                    type="datetime-local"
                    value={toZonedInput(d.fin, timezone)}
                    onChange={(e) => setDia(i, { fin: fromZonedInput(e.target.value, timezone) })}
                  />
                </label>
                <button
                  className="icon-btn"
                  onClick={() => removeDia(i)}
                  disabled={dias.length === 1}
                  title={dias.length === 1 ? 'The event needs at least one day' : 'Remove this day'}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="actions">
            <button className="btn btn-secondary" onClick={addDia}>
              + Add day
            </button>
          </div>
        </section>

        <section className="card">
          <h2>Prizes</h2>
          <p className="card-hint">
            Every segment gives Free Fuel. <strong>Extra</strong> is the prize name, and it is what the
            winner reads on the modal. <strong>On the wheel</strong> is the short version painted on the
            slice, in bold, for when the full name would not fit on a spinning wheel — leave it empty to
            paint the full name. The quota is how many go out over the whole event; leave the extra empty
            or the quota at 0 to switch a type off.
          </p>

          <div className="prize-head">
            <span>Extra</span>
            <span>On the wheel</span>
            <span>Quota</span>
            <span>Color</span>
            <span />
          </div>

          {premios.map((prize, i) => (
            <div className="prize-row" key={prize.id}>
              <input
                className="input"
                type="text"
                placeholder="e.g. + Service Club® hat"
                value={prize.label}
                onChange={(e) => patchPrize(i, { label: e.target.value })}
              />
              <input
                className="input"
                type="text"
                placeholder="same as Extra"
                value={prize.corto ?? ''}
                onChange={(e) => patchPrize(i, { corto: e.target.value })}
              />
              <input
                className="input"
                type="number"
                min={0}
                value={prize.aciertos}
                onChange={(e) => patchPrize(i, { aciertos: Math.max(0, Number(e.target.value) || 0) })}
              />
              <div className="swatch-select">
                <span className="swatch" style={{ background: SWATCH[prize.color] }} />
                <select
                  className="input"
                  value={prize.color}
                  onChange={(e) => patchPrize(i, { color: e.target.value as PrizeColor })}
                >
                  {PRIZE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="icon-btn"
                type="button"
                title="Remove prize"
                onClick={() => {
                  setPremios((list) => list.filter((_, j) => j !== i));
                  setDirty(true);
                }}
              >
                ×
              </button>
            </div>
          ))}

          <div className="actions" style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" type="button" onClick={addPrize}>
              + Add prize
            </button>
            <span className="spacer" />
            <button className="btn btn-primary" style={{ width: 'auto', marginTop: 0 }} onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </div>
          {toast && <p className={`toast toast-${toast.kind}`} style={{ marginTop: 12 }}>{toast.text}</p>}
        </section>

        <section className="card">
          <h2>Handout pace</h2>
          <p className="card-hint">
            The navy bar is what has already been handed out; the coral mark is what should have gone out by
            now. If the bar lags behind, few people are playing — the backlog carries over to the next hours
            of the <em>same</em> day, capped at twice the hourly rate so one empty hour cannot turn the next
            into a free-for-all.
          </p>
          <div className="pace-list">
            {pacing.length === 0 && <p className="pace-meta">No prize type is active.</p>}
            {pacing.map((p) => (
              <div className="pace-item" key={p.prizeId}>
                <span className="pace-name">{p.label}</span>
                <span className="pace-numbers">
                  <strong>{p.awarded}</strong> / {p.total}
                </span>
                <div className="pace-bar">
                  <div className="pace-fill" style={{ width: `${(p.awarded / p.total) * 100}%` }} />
                  <div className="pace-due" style={{ left: `calc(${(p.due / p.total) * 100}% - 1px)` }} />
                </div>
                <span className="pace-meta">
                  1 every {formatSlot(p.slotMs)} · {p.perHour.toFixed(1)}/hour · max {p.hourCap}/hour · due
                  now: {p.due} · {p.remaining} left
                </span>
                {/* Cada jornada se liquida por su cuenta: hay que poder ver
                    cuanto le tocaba y cuanto se quedo sin repartir. */}
                <ul className="pace-days">
                  {p.days.map((d, i) => (
                    <li key={d.inicio} className={d.live ? 'is-live' : d.closed ? 'is-closed' : ''}>
                      <span className="pace-day-name">
                        Day {i + 1}
                        {d.live && <em> · live</em>}
                      </span>
                      <span>
                        {d.awarded} / {d.quota}
                      </span>
                      <span className="pace-day-pending">
                        {d.closed
                          ? d.pending > 0
                            ? `${d.pending} unclaimed`
                            : 'all handed out'
                          : `${d.pending} to go`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <h2>Prizes handed out ({awards.length})</h2>
          <p className="card-hint">
            Refreshes on its own every 20 seconds. Entries cannot be edited or deleted from here:
            the winners' details are the only copy, so they only change by editing the JSON file
            on the server.
          </p>
          <ul className="award-log">
            {awards.length === 0 && (
              <li className="award-empty">None handed out yet.</li>
            )}
            {awards.map((a) => (
              <li key={a.id}>
                <div className="award-head">
                  <span>{a.label}</span>
                  <time dateTime={a.ts}>{clockStamp.format(new Date(a.ts))}</time>
                </div>
                {a.name || a.email ? (
                  <div className="award-winner">
                    <strong>{a.name}</strong>
                    {a.email && <a href={`mailto:${a.email}`}>{a.email}</a>}
                    {/* La autorizacion es lo que legitima tener estos datos:
                        se ve en la propia linea, no solo en el JSON. */}
                    {a.consent ? (
                      <span className="award-consent">✓ Authorised{claimedAt(a)}</span>
                    ) : (
                      <span className="award-consent is-missing">⚠ No authorisation on record</span>
                    )}
                  </div>
                ) : (
                  <div className="award-winner is-missing">No details left by the winner</div>
                )}
              </li>
            ))}
          </ul>
          <div className="actions">
            <button
              className="btn btn-secondary"
              onClick={exportWinners}
              disabled={awards.length === 0}
            >
              Export winners
            </button>
          </div>
        </section>

        <section className="card">
          <h2>File</h2>
          <p className="card-hint">
            The whole state is a single JSON file. You can download it, edit it by hand and upload it
            again. Importing replaces the file as a whole, log of prizes included: it is the only way
            to change or clear the winners' details, and it cannot be undone.
          </p>
          <div className="actions">
            <button className="btn btn-secondary" onClick={exportJson}>
              Export JSON
            </button>
            <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
              Import JSON
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) importJson(file);
                e.target.value = '';
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
